export interface WidgetConfigSource {
  showFullUiOnLoad?: unknown;
  resultsPerPage?: unknown;
  fullSearchUrl?: unknown;
  defaultFilter?: unknown;
  defaultSort?: unknown;
  searchText?: unknown;
  searchFields?: unknown;
  hideFacets?: unknown;
}

export interface WidgetConfig {
  showFullUiOnLoad: boolean;
  resultsPerPage: number;
  fullSearchUrl: string;
  defaultFilter: Record<string, string[]>;
  defaultSort: string;
  searchText: string;
  searchFields: 'all' | 'name' | 'name+description';
  hideFacets: boolean;
}

interface WidgetConfigWindow extends Window {
  ROEPAS_SEARCH_WIDGET_CONFIG?: WidgetConfigSource;
  APP_CONFIG?: WidgetConfigSource;
}

const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  showFullUiOnLoad: true,
  resultsPerPage: 10,
  fullSearchUrl: 'index.html',
  defaultFilter: {},
  defaultSort: 'score desc',
  searchText: '',
  searchFields: 'all',
  hideFacets: false
};

const HIDE_FACETS_DEFAULT_SORT = 'nume_str asc';

function readWindowConfig(): WidgetConfigSource {
  if (typeof window === 'undefined') {
    return {};
  }

  const globalWindow = window as WidgetConfigWindow;
  const result: WidgetConfigSource = {};
  const sources: (WidgetConfigSource | undefined)[] = [
    globalWindow.APP_CONFIG,
    globalWindow.ROEPAS_SEARCH_WIDGET_CONFIG
  ];

  sources.forEach(source => {
    if (!source || typeof source !== 'object') {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(source, 'showFullUiOnLoad')) {
      result.showFullUiOnLoad = source.showFullUiOnLoad;
    }
    if (Object.prototype.hasOwnProperty.call(source, 'resultsPerPage')) {
      result.resultsPerPage = source.resultsPerPage;
    }
    if (Object.prototype.hasOwnProperty.call(source, 'fullSearchUrl')) {
      result.fullSearchUrl = source.fullSearchUrl;
    }
    if (Object.prototype.hasOwnProperty.call(source, 'defaultFilter')) {
      result.defaultFilter = source.defaultFilter;
    }
    if (Object.prototype.hasOwnProperty.call(source, 'defaultSort')) {
      result.defaultSort = source.defaultSort;
    }
    if (Object.prototype.hasOwnProperty.call(source, 'searchText')) {
      result.searchText = source.searchText;
    }
    if (Object.prototype.hasOwnProperty.call(source, 'searchFields')) {
      result.searchFields = source.searchFields;
    }
    if (Object.prototype.hasOwnProperty.call(source, 'hideFacets')) {
      result.hideFacets = source.hideFacets;
    }
  });

  return result;
}

function normalizeBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value !== 0 : defaultValue;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return defaultValue;
    }
    if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
      return false;
    }
  }

  return defaultValue;
}

function normalizePositiveInteger(value: unknown, defaultValue: number): number {
  const parsed = typeof value === 'string' ? Number.parseInt(value, 10) : Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.floor(parsed);
  }
  return defaultValue;
}

function normalizeUrl(value: unknown, defaultValue: string): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return defaultValue;
}

// Optional token substitution applied to defaultFilter string values.
// Only values that match a KNOWN token shape are rewritten; everything else
// (including unknown {tokens}) is passed through untouched so existing
// integrations that use plain string IDs remain unaffected.
// Supported tokens:
//   {url.last}           -> last non-empty segment of location.pathname
//   {url.segment[-N]}    -> Nth segment counting from the end (-1 == last)
//   {url.segment[N]}     -> Nth segment counting from the start (0-based)
// A known token that cannot be resolved (segment missing, no window) returns
// null so the caller can drop just that value rather than send `{url.last}`
// literally to Solr.
function resolveDynamicValue(raw: string): string | null {
  if (typeof window === 'undefined') {
    return raw;
  }
  if (raw === '{url.last}') {
    const segments = window.location.pathname.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : null;
  }
  const segmentMatch = raw.match(/^\{url\.segment\[(-?\d+)\]\}$/);
  if (segmentMatch) {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const idx = Number.parseInt(segmentMatch[1], 10);
    const resolved = idx < 0 ? segments[segments.length + idx] : segments[idx];
    return resolved !== undefined && resolved !== '' ? resolved : null;
  }
  return raw;
}

function resolveDefaultFilter(raw: unknown): Record<string, string[]> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const out: Record<string, string[]> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([key, values]) => {
    if (!Array.isArray(values)) {
      return;
    }
    // Preserve the key even if the resolved array ends up empty so that
    // isFacetHidden() (which uses hasOwnProperty) keeps hiding the facet,
    // matching the previous behavior for empty-array defaults.
    out[key] = values
      .map(v => (typeof v === 'string' ? resolveDynamicValue(v) : v == null ? null : String(v)))
      .filter((v): v is string => v != null && v !== '');
  });
  return out;
}

export const WIDGET_CONFIG: WidgetConfig = (() => {
  const raw = readWindowConfig();
  const showFullUiOnLoad = normalizeBoolean(
    raw.showFullUiOnLoad,
    DEFAULT_WIDGET_CONFIG.showFullUiOnLoad
  );
  const resultsPerPage = normalizePositiveInteger(
    raw.resultsPerPage,
    DEFAULT_WIDGET_CONFIG.resultsPerPage
  );
  const fullSearchUrl = normalizeUrl(raw.fullSearchUrl, DEFAULT_WIDGET_CONFIG.fullSearchUrl);

  const defaultFilter = resolveDefaultFilter(raw.defaultFilter);

  const hideFacets = normalizeBoolean(raw.hideFacets, DEFAULT_WIDGET_CONFIG.hideFacets);

  const requestedDefaultSort = (typeof raw.defaultSort === 'string' && raw.defaultSort.trim())
    ? raw.defaultSort.trim()
    : '';

  const defaultSort = requestedDefaultSort
    ? (hideFacets && requestedDefaultSort === DEFAULT_WIDGET_CONFIG.defaultSort
      ? HIDE_FACETS_DEFAULT_SORT
      : requestedDefaultSort)
    : (hideFacets ? HIDE_FACETS_DEFAULT_SORT : DEFAULT_WIDGET_CONFIG.defaultSort);

  const searchText = (typeof raw.searchText === 'string' && raw.searchText.trim())
    ? raw.searchText.trim()
    : DEFAULT_WIDGET_CONFIG.searchText;

  const validSearchFields = ['all', 'name', 'name+description'];
  const searchFields = (typeof raw.searchFields === 'string' && validSearchFields.includes(raw.searchFields.trim()))
    ? raw.searchFields.trim() as 'all' | 'name' | 'name+description'
    : DEFAULT_WIDGET_CONFIG.searchFields;

  return {
    showFullUiOnLoad,
    resultsPerPage,
    fullSearchUrl,
    defaultFilter,
    defaultSort,
    searchText,
    searchFields,
    hideFacets
  };
})();
